import { motion } from 'framer-motion';
import {
  Target, CreditCard, Shield, Megaphone, TrendingUp, Star,
  UserCheck, Link, Building2, Users, FileText, ChevronRight, Quote
} from 'lucide-react';
import { RiHandHeartLine } from 'react-icons/ri';
import type { Service, Testimonial, TimelineItem } from '@/lib/index';

const iconMap: Record<string, React.ElementType> = {
  Target, CreditCard, Shield, Megaphone, TrendingUp, Star,
  UserCheck, Link, Building2, Users, FileText,
  Handshake: RiHandHeartLine,
};

const categoryStyle: Record<string, string> = {
  Operations:            'bg-blue-50   text-blue-700   border-blue-100',
  Fintech:               'bg-violet-50 text-violet-700 border-violet-100',
  Marketing:             'bg-amber-50  text-amber-700  border-amber-100',
  'Business Development':'bg-emerald-50 text-emerald-700 border-emerald-100',
  HR:                    'bg-rose-50   text-rose-700   border-rose-100',
};

/* ─── SERVICE CARD ─── */
interface ServiceCardProps { service: Service; index: number; }

export function ServiceCard({ service, index }: ServiceCardProps) {
  const Icon = iconMap[service.icon] || FileText;
  const cat = categoryStyle[service.category] || 'bg-secondary text-foreground border-border';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-30px' }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.25) }}
      whileHover={{ y: -3, transition: { duration: 0.18 } }}
      className="group bg-white rounded-2xl border border-border p-6
                 hover:border-primary/20 hover:shadow-lg hover:shadow-black/5
                 transition-all duration-250 cursor-default"
    >
      <div className="flex items-start justify-between mb-5">
        <div className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center
                        group-hover:bg-primary/8 transition-colors duration-250 flex-shrink-0">
          <Icon size={20} className="text-muted-foreground group-hover:text-primary transition-colors duration-250" />
        </div>
        <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${cat}`}>
          {service.category}
        </span>
      </div>
      <h3 className="font-semibold text-foreground text-[15px] leading-snug mb-2.5
                     group-hover:text-primary transition-colors duration-200">
        {service.title}
      </h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{service.description}</p>
    </motion.div>
  );
}

/* ─── STAT CARD ─── */
interface StatCardProps { value: string; label: string; index: number; }

export function StatCard({ value, label, index }: StatCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
      className="text-center"
    >
      <div className="text-4xl lg:text-5xl font-extrabold text-white mb-1.5 tracking-tight">{value}</div>
      <div className="text-white/50 text-xs font-medium tracking-wider uppercase">{label}</div>
    </motion.div>
  );
}

/* ─── WHY CARD ─── */
interface WhyCardProps { icon: React.ElementType; title: string; description: string; index: number; }

export function WhyCard({ icon: Icon, title, description, index }: WhyCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-20px' }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.07, 0.3) }}
      className="flex gap-4 p-5 rounded-2xl bg-white border border-border
                 hover:border-primary/20 hover:shadow-md hover:shadow-black/5
                 transition-all duration-250 group"
    >
      <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0
                      group-hover:bg-primary/8 transition-colors duration-250">
        <Icon size={18} className="text-primary/70 group-hover:text-primary transition-colors" />
      </div>
      <div>
        <h3 className="font-semibold text-foreground text-sm mb-1">{title}</h3>
        <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
}

/* ─── TESTIMONIAL CARD ─── */
interface TestimonialCardProps { testimonial: Testimonial; index: number; }

export function TestimonialCard({ testimonial, index }: TestimonialCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-20px' }}
      transition={{ duration: 0.45, delay: index * 0.1 }}
      className="relative bg-white border border-border rounded-2xl p-7 flex flex-col gap-5
                 hover:border-primary/15 hover:shadow-lg hover:shadow-black/5 transition-all duration-250"
    >
      <div className="absolute top-5 right-6 opacity-8">
        <Quote size={44} className="text-primary" />
      </div>
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={13} className="text-accent fill-accent" />
        ))}
      </div>
      <p className="text-foreground/75 text-sm leading-relaxed italic relative z-10">
        "{testimonial.quote}"
      </p>
      <div className="flex items-center gap-3 pt-1 border-t border-border">
        <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center
                        text-primary-foreground text-xs font-bold flex-shrink-0">
          {testimonial.initials}
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">{testimonial.name}</p>
          <p className="text-xs text-muted-foreground">{testimonial.title}, {testimonial.company}</p>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── TIMELINE CARD ─── */
interface TimelineCardProps { item: TimelineItem; index: number; isLast: boolean; }

export function TimelineCard({ item, index, isLast }: TimelineCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -16 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-20px' }}
      transition={{ duration: 0.45, delay: index * 0.08 }}
      className="relative flex gap-5"
    >
      {/* Dot & line */}
      <div className="flex flex-col items-center flex-shrink-0 pt-1">
        <div className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0
          ${item.type === 'current'
            ? 'bg-accent border-accent shadow-[0_0_0_4px_oklch(0.68_0.13_60_/_0.15)]'
            : 'bg-white border-border'}`}
        />
        {!isLast && <div className="w-px flex-1 mt-2 bg-border min-h-[32px]" />}
      </div>

      {/* Content */}
      <div className={`flex-1 ${isLast ? 'pb-0' : 'pb-7'}`}>
        <div className="flex flex-wrap items-center gap-2 mb-0.5">
          <span className="text-xs font-mono text-accent font-semibold">{item.year}</span>
          {item.type === 'current' && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent/10 text-accent border border-accent/20 font-semibold uppercase tracking-wide">
              Current
            </span>
          )}
        </div>
        <h3 className="font-bold text-foreground text-[15px] leading-snug">{item.role}</h3>
        <p className="text-primary font-semibold text-sm mt-0.5">{item.company}</p>
        <p className="text-muted-foreground/70 text-xs mb-2 italic">{item.sector}</p>
        <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
      </div>
    </motion.div>
  );
}

/* ─── SECTION BADGE ─── */
interface SectionBadgeProps { children: React.ReactNode; }
export function SectionBadge({ children }: SectionBadgeProps) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full
                     bg-secondary border border-border text-xs font-semibold
                     text-foreground/70 uppercase tracking-widest mb-5">
      {children}
    </span>
  );
}

/* ─── PRIMARY BUTTON ─── */
interface PrimaryButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  variant?: 'filled' | 'outline' | 'white';
  className?: string;
}
export function PrimaryButton({ onClick, children, variant = 'filled', className = '' }: PrimaryButtonProps) {
  const base = 'inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-200 active:scale-[0.97]';
  const variants = {
    filled:  'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md',
    outline: 'border border-border text-foreground hover:border-primary/30 hover:bg-secondary',
    white:   'bg-white text-primary hover:bg-white/90 shadow-sm hover:shadow-md',
  };
  return (
    <button onClick={onClick} className={`${base} ${variants[variant]} ${className}`}>
      {children}
      <ChevronRight size={15} className="opacity-70" />
    </button>
  );
}
